import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import handleError from "@composables/handleError";
import useMyFetch from "@stores/useMyFetch";
import { useCurrentInterviewStore } from "@stores/survey/grids/currentInterview";
import { useGridsStore } from "@stores/survey/grids/grids";
import { useSurveyStore } from "@stores/survey/survey";
import { useDebouncedSearchQuery } from "@/utils/composables/filters/useDebouncedSearchQuery";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox.js";
import type { Interview, InterviewObjective } from "@/types";
import { captureEvent } from "@/lib/analytics";

export interface GridChoice {
  rank: number;
  title: string | null;
  localized_title?: Record<string, string | null>;
}

export interface GridColumnOption {
  id: number;
  question_name: string;
  localized_title: Record<string, string | null> | null;
  card_id: number;
  item_token: string | null;
  card_token: string;
  card_type: string;
  choices: Record<string, GridChoice>;
  is_for_analytics: boolean;
}

interface RowsResponse {
  interviews?: Interview[];
  objectives?: InterviewObjective[];
  total_count?: number;
  total_pages?: number;
}

type ColumnFilters = Record<string | number, string[]>;

export const useGridRowsStore = defineStore("gridRows", () => {
  const { setCurrentInterview } = useCurrentInterviewStore();
  const gridsStore = useGridsStore();
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const rows = ref<Interview[]>([]);
  const objectives = ref<InterviewObjective[]>([]);
  const columnOptions = ref<GridColumnOption[]>([]);
  const loading = ref(false);
  const searchQuery = ref("");

  const currentPage = ref(1);
  const perPage = ref(25);
  const totalCount = ref(0);
  const totalPages = ref(0);

  const columnFilters = ref<ColumnFilters>({});

  let latestRequestId = 0;

  const fetch = async (page = currentPage.value, filters = columnFilters.value) => {
    const requestId = ++latestRequestId;
    try {
      loading.value = true;
      const params: Record<string, unknown> = {
        search_bar_input: searchQuery.value,
        page,
        per_page: perPage.value,
      };
      if (Object.keys(filters).length) params.filters = filters;

      const gridToken = gridsStore.currentGrid?.token;
      if (!gridToken) return;

      const { response, data } = await useMyFetch(`surveys/${surveyToken.value}/grids/${gridToken}/rows/search`)
        .post(params)
        .json<RowsResponse>();
      if (requestId !== latestRequestId || !response.value?.ok) return;

      rows.value = data.value?.interviews ?? [];
      objectives.value = data.value?.objectives ?? [];
      totalCount.value = data.value?.total_count ?? rows.value.length;
      totalPages.value = data.value?.total_pages ?? Math.ceil(totalCount.value / perPage.value);
      currentPage.value = page;
    } catch (err) {
      handleError(err);
    } finally {
      loading.value = false;
    }
  };

  const goToPage = async (page: number) => {
    if (page < 1 || page > totalPages.value) return;
    await fetch(page);
  };

  const selectRow = (row: Interview) => setCurrentInterview(row);

  const toggleFavorite = async (id: number) => {
    const row = rows.value.find((interview) => interview.id === id);
    if (!row) return;

    try {
      const { response } = await useMyFetch(`surveys/${surveyToken.value}/survey_customers/${id}`).patch({
        favorite: !row.favorite,
      });
      if (response.value?.ok) {
        row.favorite = !row.favorite;
        captureEvent("interview_favorited", { favorite: row.favorite });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const fetchColumnOptions = async () => {
    try {
      const { response, data } = await useMyFetch(
        `surveys/${surveyToken.value}/grids/column_options`,
      ).json<{ columns: GridColumnOption[] }>();
      if (response.value?.ok) columnOptions.value = data.value?.columns ?? [];
    } catch (err) {
      handleError(err);
    }
  };

  const { localizeText } = useSurveyLocalesStore();
  const columnLabel = (column: GridColumnOption) =>
    removeHtmlMarkdownAndLineBreak(localizeText(column.localized_title)).replaceAll("$$$", "...") || column.question_name;

  const columnOptionByName = (name: string) => columnOptions.value.find((column) => column.question_name === name);

  const ensureColumnForQuestion = async (name: string) => {
    if (gridsStore.currentGrid?.grid_type !== "respondent") return;

    const column = columnOptionByName(name);
    if (column) await gridsStore.ensureColumn(column.id);
  };

  const setFilters = async (filters: ColumnFilters) => {
    columnFilters.value = filters;
    await fetch(1, filters);
  };

  const { resetSearchQuery } = useDebouncedSearchQuery(searchQuery, async () => {
    await fetch(1);
    if (rows.value[0]) selectRow(rows.value[0]);
  });

  const reset = () => {
    resetSearchQuery();
    rows.value = [];
    objectives.value = [];
    columnOptions.value = [];
    columnFilters.value = {};
    currentPage.value = 1;
    totalCount.value = 0;
  };

  return {
    resetSearchQuery,
    reset,
    rows,
    columnLabel,
    objectives,
    columnOptions,
    loading,
    searchQuery,
    currentPage,
    perPage,
    totalCount,
    totalPages,
    columnFilters,
    fetch,
    goToPage,
    selectRow,
    toggleFavorite,
    fetchColumnOptions,
    columnOptionByName,
    ensureColumnForQuestion,
    setFilters,
  };
});
